import React, { useState, useEffect, useCallback, FC } from 'react';
import cn from 'classnames';
import acs from '@mos-utils/acs';
import { useAuthorizationStatus } from '@mos-utils/acs-react'
import Tooltip, { THEME } from '@mos-cat/tooltip';
import Modal from '@mos-cat/modal';
import ReactPlayer from 'react-player';
import { loadCameras } from '@services/loadCameras';
import { isTranslationDisabled } from '@helpers/isTranslationDisabled';
import useInterval from '@hooks/useInterval';
import { Loader } from "../../../../ds/Loader/Loader";

import st from './Translation.module.css';
import './customStyle.css';

type TranslationProps = {
  officeId?: number;
  isDefaultOffice: boolean;
  isDetailed?: boolean;
  camerasUrl?: string;
};

type CheckTranslationDisabledProps = {
  camerasLoad: any;
  setDisabled: any;
  isDefaultOffice: boolean;
  disabled: boolean;
};

const CheckTranslationDisabled: FC<CheckTranslationDisabledProps> = ({
  camerasLoad,
  setDisabled,
  isDefaultOffice,
  disabled: disabledForNow,
}) => {
  const onResponse = (data) => {
    const { serverTime } = data || { serverTime: 0 };
    const isDisabled = isTranslationDisabled(isDefaultOffice, serverTime);

    if (isDisabled !== disabledForNow) {
      setDisabled(isDisabled);
    }
  };

  useInterval(() => {
    camerasLoad(onResponse);
  }, 5 * 60 * 1000);

  return null;
};

const Translation: FC<TranslationProps> = ({
  isDetailed,
  officeId,
  camerasUrl,
  isDefaultOffice,
}) => {
  const authorizationStatus = useAuthorizationStatus();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [disabled, setDisabled] = useState(false);

  const [cameras, setCameras] = useState([]);
  const [currentCamUrl, setCurrentCamUrl] = useState('');
  const [isCameraWorks, setIsCameraWorks] = useState(false);
  const [isCamerasFiltered, setIsCamerasFiltered] = useState(false);

  const camerasId = camerasUrl.length > 0 ? camerasUrl.split('id=')[1] : '';
  const path = `/api/mfc/v1/mfcvideo/${camerasId || officeId}/`;

  const onData = (arrivedData) => {
    setData(arrivedData);
    setLoading(false);
  };

  const loadData = useCallback(() => {
    setLoading(true);
    if (authorizationStatus !== "authorized") {
      acs.login(window.location.href);
    }
    else {
      loadCameras(path, onData);
    }
  }, [path, authorizationStatus]);

  const toggleOpen = () => {
    setIsOpen(true);
    loadData();
  };

  const handleSetIsClose = () => setIsOpen(false);

  const setInitialState = () => {
    setCameras([]);
    setCurrentCamUrl('');
    setIsCameraWorks(false);
    setIsCamerasFiltered(false);
  };

  useEffect(() => {
    if (disabled) {
      setInitialState();
    } else if (!disabled && isOpen) {
      loadData();
    }
  }, [disabled, isOpen, loadData]);

  useEffect(() => {
    if (data && data.serverTime) {
      const isDisabled = isTranslationDisabled(
        isDefaultOffice,
        data.serverTime
      );

      if (isDisabled) {
        setInitialState();
        setDisabled(true);

        return;
      }
    }

    if (data && data.cameras && !isCamerasFiltered) {
      const dataCameras = data.cameras.filter((cam) => cam.status === 0);

      setCameras(dataCameras);
      setIsCamerasFiltered(true);
      setCurrentCamUrl(dataCameras.length > 0 ? dataCameras[0].HLS : '');
      setIsCameraWorks(dataCameras.length > 0);
      setDisabled(false);
    }
  }, [data, isCamerasFiltered, isDefaultOffice]);

  return (
    officeId && (
      <div className={st.wrapper}>
        <Tooltip
          wrapperClassName={st.popover}
          considerHeader
          floating
          theme={THEME.HINT}
          open={tooltipOpen}
          content="Веб-камера"
        >
          <button
            aria-label="tootip-button"
            className={cn(st.btn, { [st.btnDetailed]: isDetailed })}
            type="button"
            onMouseOver={() => setTooltipOpen(true)}
            onMouseOut={() => setTooltipOpen(false)}
            onFocus={() => setTooltipOpen(false)}
            onBlur={() => setTooltipOpen(false)}
            onClick={toggleOpen}
          />
        </Tooltip>
        <Modal
          className={st.modal}
          headerContent={<h2 className={st.title}>Веб-камера</h2>}
          visible={isOpen}
          onClose={handleSetIsClose}
        >
          <section className={st.content}>
            {!loading && isOpen && (
              <CheckTranslationDisabled
                disabled={disabled}
                isDefaultOffice={isDefaultOffice}
                setDisabled={setDisabled}
                camerasLoad={(onResponse) => loadCameras(path, onResponse)}
              />
            )}
            {loading ? (
              <Loader overlayVisible={false} />
            ) : (
              <>
                <div className={st.toggleList}>
                  {cameras && !disabled
                    ? cameras.map((cam) => {
                        const activeListItem = currentCamUrl === cam.HLS;

                        if (cam.status === 0) {
                          return (
                            <button
                              type="button"
                              key={cam.HLS}
                              onClick={() => {
                                setCurrentCamUrl(cam.HLS);
                              }}
                              className={
                                activeListItem ? st.listItemActive : st.listItem
                              }
                            >
                              {cam.name}
                            </button>
                          );
                        }

                        return null;
                      })
                    : null}
                </div>
                {isOpen && (
                  <ReactPlayer
                    playing
                    width="100%"
                    volume={0}
                    url={currentCamUrl}
                    key={currentCamUrl}
                    controls
                    className={`${st.playerWrap} ${
                      isCameraWorks && !disabled ? '' : st.cameraNotWorks
                    }`}
                  />
                )}
              </>
            )}
          </section>
        </Modal>
      </div>
    )
  );
};

export default Translation;
